import { createFileRoute, Link } from "@tanstack/react-router";
import * as React from "react";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import { PortalLayout } from "@/components/portal-layout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/teacher/analytics")({
  component: AnalyticsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    filter: (search.filter as string) || undefined,
  }),
});

function AnalyticsPage() {
  const { user } = useAuth();
  const { filter } = Route.useSearch();
  const [filterClass, setFilterClass] = React.useState("all");
  const [filterStudent, setFilterStudent] = React.useState("all");
  const [searchQuery, setSearchQuery] = React.useState("");

  const classesQ = useQuery({
    queryKey: ["teacher-analytics-classes", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from("classes").select("id, name").eq("teacher_id", user!.id);
      return data ?? [];
    },
  });

  const classIds = React.useMemo(() => {
    if (filterClass !== "all") return [filterClass];
    return (classesQ.data ?? []).map((c) => c.id);
  }, [filterClass, classesQ.data]);

  const studentsQ = useQuery({
    queryKey: ["teacher-analytics-students", classIds.join(",")],
    enabled: classIds.length > 0,
    queryFn: async () => {
      const { data: enrolls } = await supabase
        .from("enrollments").select("student_id, class_id")
        .in("class_id", classIds).eq("is_active", true);
      const ids = Array.from(new Set((enrolls ?? []).map((e) => e.student_id)));
      if (!ids.length) return [] as Array<{ id: string; display_name: string | null; email: string; className: string }>;
      const [{ data: profs }, classMap] = [
        await supabase.from("profiles").select("id, display_name, email").in("id", ids),
        new Map((classesQ.data ?? []).map(c => [c.id, c.name])),
      ];
      const classByStudent = new Map<string, string>();
      (enrolls ?? []).forEach(e => {
        if (!classByStudent.has(e.student_id)) classByStudent.set(e.student_id, classMap.get(e.class_id) ?? "—");
      });
      return (profs ?? []).map(p => ({ ...p, className: classByStudent.get(p.id) ?? "—" }));
    },
  });

  // Attention list across ALL teacher classes (independent of filterClass)
  const attentionQ = useQuery({
    queryKey: ["teacher-attention-ids", user?.id],
    enabled: !!user && filter === "attention",
    queryFn: async () => {
      const allIds = (classesQ.data ?? []).map(c => c.id);
      if (!allIds.length) return new Set<string>();
      const { data: sessions } = await supabase.from("class_sessions").select("id").in("class_id", allIds);
      const sessionIds = (sessions ?? []).map(s => s.id);
      const { data: atts } = sessionIds.length
        ? await supabase.from("attendances").select("student_id, status").in("session_id", sessionIds)
        : { data: [] as { student_id: string; status: string }[] };
      const { data: assigns } = await supabase.from("assignments").select("id").in("class_id", allIds);
      const assignIds = (assigns ?? []).map(a => a.id);
      const { data: subs } = assignIds.length
        ? await supabase.from("submissions").select("student_id, score").in("assignment_id", assignIds).not("score", "is", null)
        : { data: [] as { student_id: string; score: number | null }[] };

      const byStudent = new Map<string, { total: number; present: number }>();
      (atts ?? []).forEach(a => {
        const cur = byStudent.get(a.student_id) ?? { total: 0, present: 0 };
        cur.total++;
        if (a.status === "present" || a.status === "late") cur.present++;
        byStudent.set(a.student_id, cur);
      });
      const scoreByStudent = new Map<string, { total: number; sum: number }>();
      (subs ?? []).forEach(s => {
        if (s.score === null) return;
        const cur = scoreByStudent.get(s.student_id) ?? { total: 0, sum: 0 };
        cur.total++; cur.sum += Number(s.score);
        scoreByStudent.set(s.student_id, cur);
      });
      const attentionSet = new Set<string>();
      new Set([...byStudent.keys(), ...scoreByStudent.keys()]).forEach(sid => {
        const v = byStudent.get(sid);
        const attRate = v && v.total > 0 ? v.present / v.total : 1;
        const sc = scoreByStudent.get(sid);
        const avg = sc ? sc.sum / sc.total : 10;
        if ((v && v.total >= 3 && attRate < 0.5) || (sc && avg < 5)) attentionSet.add(sid);
      });
      return attentionSet;
    },
  });

  const filteredStudents = React.useMemo(() => {
    let list = studentsQ.data ?? [];
    if (filter === "attention" && attentionQ.data) {
      list = list.filter(s => attentionQ.data!.has(s.id));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(s => (s.display_name ?? s.email).toLowerCase().includes(q));
    }
    return list;
  }, [studentsQ.data, attentionQ.data, filter, searchQuery]);

  const statsQ = useQuery({
    queryKey: ["teacher-analytics-stats", classIds.join(","), filterStudent],
    enabled: !!user && classIds.length > 0,
    queryFn: async () => {
      // Sessions in scope
      const { data: sessions } = await supabase
        .from("class_sessions").select("id, class_id").in("class_id", classIds);
      const sessionIds = (sessions ?? []).map((s) => s.id);

      // Attendances
      let attQ = supabase.from("attendances").select("status, student_id, session_id").in("session_id", sessionIds.length ? sessionIds : ["00000000-0000-0000-0000-000000000000"]);
      if (filterStudent !== "all") attQ = attQ.eq("student_id", filterStudent);
      const { data: atts } = await attQ;

      // Assignments
      const { data: assigns } = await supabase
        .from("assignments").select("id, title, class_id, max_score").in("class_id", classIds);
      const assignIds = (assigns ?? []).map((a) => a.id);

      // Submissions
      let subQ = supabase.from("submissions").select("assignment_id, student_id, score, submitted_at")
        .in("assignment_id", assignIds.length ? assignIds : ["00000000-0000-0000-0000-000000000000"]);
      if (filterStudent !== "all") subQ = subQ.eq("student_id", filterStudent);
      const { data: subs } = await subQ;

      return {
        sessions: sessions ?? [],
        atts: atts ?? [],
        assigns: assigns ?? [],
        subs: subs ?? [],
      };
    },
  });

  const data = statsQ.data;
  const totalAtt = data?.atts.length ?? 0;
  const presentCount = data?.atts.filter((a) => a.status === "present" || a.status === "late").length ?? 0;
  const attRate = totalAtt > 0 ? Math.round((presentCount / totalAtt) * 100) : 0;
  const scoredSubs = (data?.subs ?? []).filter((s) => s.score !== null);
  const avgScore = scoredSubs.length > 0
    ? (scoredSubs.reduce((sum, s) => sum + Number(s.score), 0) / scoredSubs.length).toFixed(1)
    : "—";
  const assignCount = data?.assigns.length ?? 0;
  const submittedCount = data?.subs.length ?? 0;

  // Per-class chart (when "all" classes)
  const perClassData = React.useMemo(() => {
    if (!data || filterClass !== "all") return [];
    return (classesQ.data ?? []).map((c) => {
      const cAssignIds = data.assigns.filter((a) => a.class_id === c.id).map((a) => a.id);
      const cSubs = data.subs.filter((s) => cAssignIds.includes(s.assignment_id) && s.score !== null);
      const avg = cSubs.length > 0
        ? Number((cSubs.reduce((sum, s) => sum + Number(s.score), 0) / cSubs.length).toFixed(1))
        : 0;
      return { name: c.name, avg };
    });
  }, [data, classesQ.data, filterClass]);

  // Per-assignment scores when a single student selected
  const studentScores = React.useMemo(() => {
    if (filterStudent === "all" || !data) return [];
    return data.subs
      .filter((s) => s.score !== null)
      .map((s) => {
        const a = data.assigns.find((x) => x.id === s.assignment_id);
        return {
          name: a?.title ?? "—",
          score: Number(s.score),
          max: a?.max_score ?? 10,
          date: s.submitted_at,
        };
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [data, filterStudent]);

  return (
    <PortalLayout portal="teacher" title="Thống kê lớp học" breadcrumb={[{ label: "Giáo viên", to: "/teacher" }, { label: "Thống kê" }]}>
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <Select value={filterClass} onValueChange={(v) => { setFilterClass(v); setFilterStudent("all"); }}>
          <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tất cả lớp</SelectItem>
            {(classesQ.data ?? []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>

        <Input
          placeholder="Tìm học sinh theo tên..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-56"
        />

        <Select value={filterStudent} onValueChange={setFilterStudent}>
          <SelectTrigger className="w-64"><SelectValue placeholder="Học sinh" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tất cả học sinh</SelectItem>
            {filteredStudents.map((s) => {
              const isAttention = attentionQ.data?.has(s.id);
              return (
                <SelectItem key={s.id} value={s.id}>
                  <div className="flex items-center gap-2">
                    <span>{s.display_name ?? s.email.split("@")[0]}</span>
                    <Badge variant="secondary" className="text-[10px]">{s.className}</Badge>
                    {isAttention && <Badge variant="destructive" className="text-[10px]">⚠️</Badge>}
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        {filter === "attention" && (
          <div className="flex items-center gap-2">
            <Badge variant="destructive" className="text-xs gap-1">⚠️ Đang xem: HS cần chú ý</Badge>
            <Link to="/teacher/analytics" search={{ filter: undefined }} className="text-xs text-blue-600 hover:underline">
              Xem tất cả
            </Link>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatBox label="Tỉ lệ điểm danh" value={`${attRate}%`} sub={`${presentCount}/${totalAtt} lượt`} />
        <StatBox label="Điểm trung bình" value={String(avgScore)} sub={`${scoredSubs.length} bài đã chấm`} />
        <StatBox label="Số bài tập" value={String(assignCount)} sub="trong phạm vi" />
        <StatBox label="Lượt nộp" value={String(submittedCount)} sub="tổng cộng" />
      </div>

      {filterClass === "all" && perClassData.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="bg-card border rounded-2xl p-6 shadow-sm mb-6">
          <h3 className="font-display font-semibold mb-1">Điểm trung bình theo lớp</h3>
          <p className="text-xs text-muted-foreground mb-3">So sánh giữa các lớp bạn dạy</p>
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={perClassData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.92 0.01 255)" horizontal={false} />
                <XAxis type="number" domain={[0, 10]} fontSize={11} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" fontSize={12} axisLine={false} tickLine={false} width={120} />
                <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                <Bar dataKey="avg" fill="#1A6B3C" radius={[0, 8, 8, 0]} animationDuration={900} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      )}

      {filterStudent !== "all" && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="bg-card border rounded-2xl p-6 shadow-sm">
          <h3 className="font-display font-semibold mb-1">Điểm theo từng bài</h3>
          <p className="text-xs text-muted-foreground mb-4">Lịch sử điểm của học sinh</p>
          {studentScores.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">Chưa có điểm nào</p>
          ) : (
            <div className="space-y-2">
              {studentScores.map((s, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg border">
                  <div>
                    <div className="text-sm font-medium">{s.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {format(new Date(s.date), "dd/MM/yyyy HH:mm", { locale: vi })}
                    </div>
                  </div>
                  <Badge style={{ background: "var(--portal)", color: "white" }}>
                    {s.score}/{s.max}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {statsQ.isLoading && <div className="text-sm text-muted-foreground text-center py-6">Đang tải dữ liệu...</div>}
    </PortalLayout>
  );
}

function StatBox({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="bg-card border rounded-2xl p-5 shadow-sm">
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className="text-2xl font-display font-semibold" style={{ color: "var(--portal)" }}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </motion.div>
  );
}
